import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import useApiService from 'services/api'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import {
  DealSearchResult,
  listMyCompanies,
  searchDeals,
  searchVariants,
  VariantSearchResult,
} from 'services/search'
import { transformFirstBiteResults } from '../utils/searchUtils'
import { Filters, MIN_SEARCH_CHARS, UnifiedSearchTab } from '../constants'

// Helper to extract value from settled promise or return default empty response
function getSettledValue<T>(result: PromiseSettledResult<T>): T {
  if (result.status === 'fulfilled') {
    return result.value
  }
  return { results: [], total: 0 } as T
}

export type SearchResultUnion =
  | VariantSearchResult
  | DealSearchResult
  | ContactCompanySearchResult
  | DoorSearchResult

export type SearchType = 'address' | 'domain' | 'name' | 'sku'

interface SearchResults {
  firstBiteResults: ContactCompanySearchResult[]
  unmatchedResults: ContactCompanySearchResult[]
  firstBiteDoorResults: any[]
  unmatchedDoorResults: any[]
  variantResults: VariantSearchResult[]
  dealResults: DealSearchResult[]
  myCompaniesResults: ContactCompanySearchResult[]
  firstBiteCount: number
  unmatchedCount: number
  firstBiteDoorCount: number
  unmatchedDoorCount: number
  variantCount: number
  dealCount: number
  myCompaniesCount: number
  firstBiteTotalRows: number
  unmatchedTotalRows: number
  firstBiteDoorTotalRows: number
  unmatchedDoorTotalRows: number
  variantTotalRows: number
  dealTotalRows: number
  myCompaniesTotalRows: number
  isLoading: boolean
  myCompaniesLoading: boolean
  currentResults: SearchResultUnion[]
  currentTotalRows: number
  searchType: SearchType | null
  refetchVariants: () => void
  clearResults: () => void
}

interface UseUnifiedSearchAPIProps {
  searchTerm: string
  filters: Filters
  sortBy: string
  firstBitePage: number
  unmatchedPage: number
  firstBiteDoorPage: number
  unmatchedDoorPage: number
  variantPage: number
  dealPage: number
  selectedTab: UnifiedSearchTab
  pageSize: number
  // Why: avoid fetching the user's custom-company list from surfaces that
  // never render the "My Companies" tab (UnifiedSearchModal, document modals).
  myCompaniesEnabled?: boolean
}

export function useUnifiedSearchAPI({
  searchTerm,
  filters,
  sortBy,
  firstBitePage,
  unmatchedPage,
  firstBiteDoorPage,
  unmatchedDoorPage,
  variantPage,
  dealPage,
  selectedTab,
  pageSize,
  myCompaniesEnabled = false,
}: UseUnifiedSearchAPIProps): SearchResults {
  const api = useApiService()
  const apiRef = useRef(api)
  apiRef.current = api

  // State for search results
  const [isLoading, setIsLoading] = useState(false)
  const [firstBiteResults, setFirstBiteResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [unmatchedResults, setUnmatchedResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [firstBiteDoorResults, setFirstBiteDoorResults] = useState<
    DoorSearchResult[]
  >([])
  const [unmatchedDoorResults, setUnmatchedDoorResults] = useState<
    DoorSearchResult[]
  >([])
  const [variantResults, setVariantResults] = useState<VariantSearchResult[]>(
    []
  )
  const [dealResults, setDealResults] = useState<DealSearchResult[]>([])
  const [myCompaniesResults, setMyCompaniesResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [firstBiteCount, setFirstBiteCount] = useState(0)
  const [unmatchedCount, setUnmatchedCount] = useState(0)
  const [firstBiteDoorCount, setFirstBiteDoorCount] = useState(0)
  const [unmatchedDoorCount, setUnmatchedDoorCount] = useState(0)
  const [variantCount, setVariantCount] = useState(0)
  const [dealCount, setDealCount] = useState(0)
  const [myCompaniesCount, setMyCompaniesCount] = useState(0)
  const [firstBiteTotalRows, setFirstBiteTotalRows] = useState(0)
  const [unmatchedTotalRows, setUnmatchedTotalRows] = useState(0)
  const [firstBiteDoorTotalRows, setFirstBiteDoorTotalRows] = useState(0)
  const [unmatchedDoorTotalRows, setUnmatchedDoorTotalRows] = useState(0)
  const [variantTotalRows, setVariantTotalRows] = useState(0)
  const [dealTotalRows, setDealTotalRows] = useState(0)
  const [myCompaniesTotalRows, setMyCompaniesTotalRows] = useState(0)
  const [myCompaniesLoading, setMyCompaniesLoading] = useState(false)
  const [searchType, setSearchType] = useState<SearchType | null>(null)

  // Build search options for both tabs
  const firstBiteSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (firstBitePage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, firstBitePage])

  const unmatchedSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (unmatchedPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, unmatchedPage])

  const firstBiteDoorSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (firstBiteDoorPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, firstBiteDoorPage])

  const unmatchedDoorSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (unmatchedDoorPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, unmatchedDoorPage])

  const variantSearchOptions = useMemo(() => {
    const options: { limit: number; offset: number; product_id?: number } = {
      limit: pageSize,
      offset: (variantPage - 1) * pageSize,
    }
    // Add product filter if selected
    if (filters.product && filters.product !== 'all') {
      options.product_id = parseInt(filters.product, 10)
    }
    return options
  }, [pageSize, variantPage, filters.product])

  const dealSearchOptions = useMemo(() => {
    return {
      limit: pageSize,
      offset: (dealPage - 1) * pageSize,
    }
  }, [pageSize, dealPage])

  // Refetch variants only (for use after edit/delete)
  const refetchVariants = useCallback(async () => {
    if (!searchTerm.trim() || searchTerm.length < MIN_SEARCH_CHARS) return

    try {
      const variantData = await searchVariants(searchTerm, variantSearchOptions)
      const variantResultsData = variantData.results || []
      const variantTotal = variantData.total || 0

      setVariantResults(variantResultsData)
      setVariantCount(variantTotal)
      setVariantTotalRows(variantTotal)
    } catch (error) {
      console.error('Error refetching variants:', error)
    }
  }, [searchTerm, variantSearchOptions])

  // Single search function that handles all tabs
  const performSearch = useCallback(
    async (
      searchValue: string,
      fbOptions: any,
      umOptions: any,
      fbDoorOptions: any,
      umDoorOptions: any,
      varOptions: { limit?: number; offset?: number },
      dlOptions: { limit?: number; offset?: number }
    ) => {
      if (!searchValue.trim()) {
        setFirstBiteResults([])
        setUnmatchedResults([])
        setFirstBiteDoorResults([])
        setUnmatchedDoorResults([])
        setVariantResults([])
        setDealResults([])
        setFirstBiteCount(0)
        setUnmatchedCount(0)
        setFirstBiteDoorCount(0)
        setUnmatchedDoorCount(0)
        setVariantCount(0)
        setDealCount(0)
        setFirstBiteTotalRows(0)
        setUnmatchedTotalRows(0)
        setFirstBiteDoorTotalRows(0)
        setUnmatchedDoorTotalRows(0)
        setVariantTotalRows(0)
        setDealTotalRows(0)
        setSearchType(null)
        return
      }

      try {
        setIsLoading(true)
        // Clear the search type when starting a new search
        setSearchType(null)

        const currentApi = apiRef.current
        const results = await Promise.allSettled([
          currentApi.searchFirstBiteCompanies(searchValue, fbOptions),
          currentApi.searchUnmatchedCompanies(searchValue, umOptions),
          currentApi.searchFirstBiteDoors(searchValue, fbDoorOptions),
          currentApi.searchUnmatchedDoors(searchValue, umDoorOptions),
          searchVariants(searchValue, varOptions),
          searchDeals({ q: searchValue, ...dlOptions }),
        ])

        const firstBiteData = getSettledValue(results[0])
        const unmatchedData = getSettledValue(results[1])
        const firstBiteDoorData = getSettledValue(results[2])
        const unmatchedDoorData = getSettledValue(results[3])
        const variantData = getSettledValue(results[4])
        const dealData = getSettledValue(results[5])

        // Handle response format
        const firstBiteResults = firstBiteData.results || []
        const unmatchedResults = unmatchedData.results || []
        const firstBiteDoorResults = firstBiteDoorData.results || []
        const unmatchedDoorResults = unmatchedDoorData.results || []
        const variantResultsData = variantData.results || []
        const dealResultsData = dealData.results || []
        const firstBiteTotal = firstBiteData.total || 0
        const unmatchedTotal = unmatchedData.total || 0
        const firstBiteDoorTotal = firstBiteDoorData.total || 0
        const unmatchedDoorTotal = unmatchedDoorData.total || 0
        const variantTotal = variantData.total || 0
        const dealTotal = dealData.total || 0

        // Get search type from any of the responses (they should all be the same)
        const detectedSearchType =
          firstBiteData.search_type ||
          unmatchedData.search_type ||
          firstBiteDoorData.search_type ||
          unmatchedDoorData.search_type ||
          'name'

        // Transform First Bite results
        const transformedFirstBite = transformFirstBiteResults(firstBiteResults)

        setFirstBiteResults(transformedFirstBite)
        setUnmatchedResults(unmatchedResults)
        setFirstBiteDoorResults(firstBiteDoorResults)
        setUnmatchedDoorResults(unmatchedDoorResults)
        setVariantResults(variantResultsData)
        setDealResults(dealResultsData)
        setFirstBiteCount(firstBiteTotal)
        setUnmatchedCount(unmatchedTotal)
        setFirstBiteDoorCount(firstBiteDoorTotal)
        setUnmatchedDoorCount(unmatchedDoorTotal)
        setVariantCount(variantTotal)
        setDealCount(dealTotal)
        setFirstBiteTotalRows(firstBiteTotal)
        setUnmatchedTotalRows(unmatchedTotal)
        setFirstBiteDoorTotalRows(firstBiteDoorTotal)
        setUnmatchedDoorTotalRows(unmatchedDoorTotal)
        setVariantTotalRows(variantTotal)
        setDealTotalRows(dealTotal)
        setSearchType(detectedSearchType as SearchType)
      } catch (error) {
        // Reset on error
        setFirstBiteResults([])
        setUnmatchedResults([])
        setFirstBiteDoorResults([])
        setUnmatchedDoorResults([])
        setVariantResults([])
        setDealResults([])
        setFirstBiteCount(0)
        setUnmatchedCount(0)
        setFirstBiteDoorCount(0)
        setUnmatchedDoorCount(0)
        setVariantCount(0)
        setDealCount(0)
        setFirstBiteTotalRows(0)
        setUnmatchedTotalRows(0)
        setFirstBiteDoorTotalRows(0)
        setUnmatchedDoorTotalRows(0)
        setVariantTotalRows(0)
        setDealTotalRows(0)
        setSearchType(null)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const clearResults = useCallback(() => {
    setFirstBiteResults([])
    setUnmatchedResults([])
    setFirstBiteDoorResults([])
    setUnmatchedDoorResults([])
    setVariantResults([])
    setDealResults([])
    setFirstBiteCount(0)
    setUnmatchedCount(0)
    setFirstBiteDoorCount(0)
    setUnmatchedDoorCount(0)
    setVariantCount(0)
    setDealCount(0)
    setFirstBiteTotalRows(0)
    setUnmatchedTotalRows(0)
    setFirstBiteDoorTotalRows(0)
    setUnmatchedDoorTotalRows(0)
    setVariantTotalRows(0)
    setDealTotalRows(0)
    setSearchType(null)
  }, [])

  // My Companies — independent of the search bar. Fetches once when the
  // parent opts in via myCompaniesEnabled; sort changes only refetch when
  // the user is actively on the mycompanies tab so re-sorting FB / Custom
  // Companies doesn't fire wasted requests.
  const lastMyCompaniesSortRef = useRef<string | null>(null)
  useEffect(() => {
    if (!myCompaniesEnabled) return
    const isInitial = lastMyCompaniesSortRef.current === null
    const sortChangedOnActiveTab =
      lastMyCompaniesSortRef.current !== sortBy && selectedTab === 'mycompanies'
    if (!isInitial && !sortChangedOnActiveTab) return
    lastMyCompaniesSortRef.current = sortBy

    let cancelled = false
    const FETCH_LIMIT = 200
    setMyCompaniesLoading(true)
    listMyCompanies({ sort_by: sortBy, limit: FETCH_LIMIT, offset: 0 })
      .then((data) => {
        if (cancelled) return
        const results = data.results || []
        setMyCompaniesResults(results)
        // Badge tracks browseable rows, not server total — pagination for
        // >FETCH_LIMIT lists is a follow-up.
        const browseable = Math.min(data.total || 0, results.length)
        setMyCompaniesCount(browseable)
        setMyCompaniesTotalRows(browseable)
      })
      .catch(() => {
        if (cancelled) return
        setMyCompaniesResults([])
        setMyCompaniesCount(0)
        setMyCompaniesTotalRows(0)
      })
      .finally(() => {
        if (cancelled) return
        setMyCompaniesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sortBy, myCompaniesEnabled, selectedTab])

  // Trigger search when search term or options change
  useEffect(() => {
    if (searchTerm.length >= MIN_SEARCH_CHARS) {
      performSearch(
        searchTerm,
        firstBiteSearchOptions,
        unmatchedSearchOptions,
        firstBiteDoorSearchOptions,
        unmatchedDoorSearchOptions,
        variantSearchOptions,
        dealSearchOptions
      )
    } else {
      setFirstBiteResults([])
      setUnmatchedResults([])
      setFirstBiteDoorResults([])
      setUnmatchedDoorResults([])
      setVariantResults([])
      setDealResults([])
      setFirstBiteCount(0)
      setUnmatchedCount(0)
      setFirstBiteDoorCount(0)
      setUnmatchedDoorCount(0)
      setVariantCount(0)
      setDealCount(0)
      setFirstBiteTotalRows(0)
      setUnmatchedTotalRows(0)
      setFirstBiteDoorTotalRows(0)
      setUnmatchedDoorTotalRows(0)
      setVariantTotalRows(0)
      setDealTotalRows(0)
    }
  }, [
    searchTerm,
    firstBiteSearchOptions,
    unmatchedSearchOptions,
    firstBiteDoorSearchOptions,
    unmatchedDoorSearchOptions,
    variantSearchOptions,
    dealSearchOptions,
    performSearch,
  ])

  // Handle client-side sorting for non-relevance sorts
  useEffect(() => {
    if (sortBy === 'relevance') return

    const sortResults = <T extends { name: string; size?: number }>(
      results: T[]
    ): T[] => {
      return [...results].sort((a, b) => {
        switch (sortBy) {
          case 'alphabetical_asc':
          case 'alphabetical':
            return a.name.localeCompare(b.name)
          case 'alphabetical_desc':
            return b.name.localeCompare(a.name)
          case 'size_desc':
          case 'size':
            return (b.size || 0) - (a.size || 0)
          case 'size_asc':
            return (a.size || 0) - (b.size || 0)
          default:
            return 0
        }
      })
    }

    if (firstBiteResults.length > 0) {
      setFirstBiteResults(sortResults(firstBiteResults))
    }
    if (unmatchedResults.length > 0) {
      setUnmatchedResults(sortResults(unmatchedResults))
    }
    if (firstBiteDoorResults.length > 0) {
      setFirstBiteDoorResults(sortResults(firstBiteDoorResults))
    }
    if (unmatchedDoorResults.length > 0) {
      setUnmatchedDoorResults(sortResults(unmatchedDoorResults))
    }
  }, [
    sortBy,
    firstBiteResults.length,
    unmatchedResults.length,
    firstBiteDoorResults.length,
    unmatchedDoorResults.length,
  ])

  // Get current results based on selected tab
  const currentResults =
    selectedTab === 'mycompanies'
      ? myCompaniesResults
      : selectedTab === 'firstbite'
        ? firstBiteResults
        : selectedTab === 'firstbitedoors'
          ? firstBiteDoorResults
          : selectedTab === 'unmatcheddoors'
            ? unmatchedDoorResults
            : selectedTab === 'variants'
              ? variantResults
              : selectedTab === 'deals'
                ? dealResults
                : unmatchedResults
  const currentTotalRows =
    selectedTab === 'mycompanies'
      ? myCompaniesTotalRows
      : selectedTab === 'firstbite'
        ? firstBiteTotalRows
        : selectedTab === 'firstbitedoors'
          ? firstBiteDoorTotalRows
          : selectedTab === 'unmatcheddoors'
            ? unmatchedDoorTotalRows
            : selectedTab === 'variants'
              ? variantTotalRows
              : selectedTab === 'deals'
                ? dealTotalRows
                : unmatchedTotalRows

  return {
    firstBiteResults,
    unmatchedResults,
    firstBiteDoorResults,
    unmatchedDoorResults,
    variantResults,
    dealResults,
    myCompaniesResults,
    firstBiteCount,
    unmatchedCount,
    firstBiteDoorCount,
    unmatchedDoorCount,
    variantCount,
    dealCount,
    myCompaniesCount,
    firstBiteTotalRows,
    unmatchedTotalRows,
    firstBiteDoorTotalRows,
    unmatchedDoorTotalRows,
    variantTotalRows,
    dealTotalRows,
    myCompaniesTotalRows,
    isLoading,
    myCompaniesLoading,
    currentResults,
    currentTotalRows,
    searchType,
    refetchVariants,
    clearResults,
  }
}
